import { useEffect, useState } from 'react';
import { api } from '../../../api/client';
import styles from './SettingsPage.module.css';

interface Grade {
  id: string;
  name: string;
  groups: { id: string; name: string }[];
}
interface SchoolData {
  id: string;
  name: string;
  slug: string;
  plan: string;
  settings: string;
  modules: Record<string, boolean>;
  stats: { totalStudents: number; totalStaff: number; totalGroups: number; totalPayments: number };
}
interface SchoolSettings {
  schoolYearStart: string;
  schoolYearEnd: string;
  studentVacationStart: string;
  studentVacationEnd: string;
  staffVacationStart: string;
  staffVacationEnd: string;
  tuitionCost: string;
  activeMonths: string[];
}

const MONTH_LABELS = [
  'Enero',
  'Febrero',
  'Marzo',
  'Abril',
  'Mayo',
  'Junio',
  'Julio',
  'Agosto',
  'Septiembre',
  'Octubre',
  'Noviembre',
  'Diciembre',
];

function getMonthsBetween(start: string, end: string): string[] {
  if (!start || !end) return [];
  const months: string[] = [];
  const d = new Date(start);
  const endDate = new Date(end);
  while (d <= endDate) {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    months.push(`${y}-${m}`);
    d.setMonth(d.getMonth() + 1);
  }
  return months;
}

function parseSettings(raw: string): SchoolSettings {
  try {
    const s = JSON.parse(raw);
    return {
      schoolYearStart: s.schoolYearStart || '',
      schoolYearEnd: s.schoolYearEnd || '',
      studentVacationStart: s.studentVacationStart || '',
      studentVacationEnd: s.studentVacationEnd || '',
      staffVacationStart: s.staffVacationStart || '',
      staffVacationEnd: s.staffVacationEnd || '',
      tuitionCost: s.tuitionCost || '',
      activeMonths: Array.isArray(s.activeMonths) ? s.activeMonths : [],
    };
  } catch {
    return {
      schoolYearStart: '',
      schoolYearEnd: '',
      studentVacationStart: '',
      studentVacationEnd: '',
      staffVacationStart: '',
      staffVacationEnd: '',
      tuitionCost: '',
      activeMonths: [],
    };
  }
}

export default function SettingsPage() {
  const [school, setSchool] = useState<SchoolData | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState<SchoolSettings>(parseSettings(''));
  const [message, setMessage] = useState('');

  const [grades, setGrades] = useState<Grade[]>([]);
  const [newGrade, setNewGrade] = useState('');
  const [newGroup, setNewGroup] = useState('');
  const [newGroupGrade, setNewGroupGrade] = useState('');
  const [loadingGrades, setLoadingGrades] = useState(false);
  const [gradeMsg, setGradeMsg] = useState('');

  const fetchSchool = async () => {
    setLoading(true);
    try {
      const data = await api.get<any>('/api/school');
      setSchool(data);
      setSettings(parseSettings(data.settings || '{}'));
    } catch (e) {
      console.error(e);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchSchool();
  }, []);

  // Auto-populate months when dates change and no months are configured
  useEffect(() => {
    if (settings.schoolYearStart && settings.schoolYearEnd && settings.activeMonths.length === 0) {
      const all = getMonthsBetween(settings.schoolYearStart, settings.schoolYearEnd);
      if (all.length > 0) updateSetting('activeMonths', all);
    }
  }, [settings.schoolYearStart, settings.schoolYearEnd]);

  const toggleMonth = (ym: string) => {
    setSettings((prev) => ({
      ...prev,
      activeMonths: prev.activeMonths.includes(ym)
        ? prev.activeMonths.filter((m) => m !== ym)
        : [...prev.activeMonths, ym].sort(),
    }));
  };

  const activeMonthLabels = settings.activeMonths.map((ym) => {
    const [y, m] = ym.split('-');
    return { ym, label: `${MONTH_LABELS[parseInt(m) - 1]} ${y}` };
  });

  const fetchGrades = async () => {
    setLoadingGrades(true);
    try {
      const data = await api.get<any[]>('/api/school/grades');
      if (Array.isArray(data)) setGrades(data);
    } catch {
      /* ignore */
    }
    setLoadingGrades(false);
  };

  useEffect(() => {
    fetchGrades();
  }, []);

  const updateSetting = <K extends keyof SchoolSettings>(key: K, value: SchoolSettings[K]) => {
    setSettings((prev) => ({ ...prev, [key]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage('');
    try {
      await api.put('/api/school', { settings: JSON.stringify(settings) });
      setMessage('Configuración guardada exitosamente');
      fetchSchool();
    } catch {
      setMessage('Error al guardar la configuración');
    } finally {
      setSaving(false);
    }
  };

  const addGrade = async () => {
    if (!newGrade.trim()) return;
    setGradeMsg('');
    try {
      await api.post('/api/school/grades', { name: newGrade });
      setNewGrade('');
      setGradeMsg('Grado agregado');
      fetchGrades();
    } catch (err: any) {
      setGradeMsg(err.message);
    }
  };

  const deleteGrade = async (id: string) => {
    if (!confirm('¿Eliminar este grado y todos sus grupos?')) return;
    try {
      await api.delete('/api/school/grades', { body: { id } });
      fetchGrades();
    } catch {
      /* ignore */
    }
  };

  const addGroup = async () => {
    if (!newGroup.trim() || !newGroupGrade) return;
    setGradeMsg('');
    try {
      await api.post(`/api/school/grades/${newGroupGrade}/groups`, { name: newGroup });
      setNewGroup('');
      setGradeMsg('Grupo agregado');
      fetchGrades();
    } catch (err: any) {
      setGradeMsg(err.message);
    }
  };

  const deleteGroup = async (gradeId: string, groupId: string) => {
    if (!confirm('¿Eliminar este grupo?')) return;
    try {
      await api.delete(`/api/school/groups/${groupId}`);
      fetchGrades();
    } catch {
      /* ignore */
    }
  };

  if (loading)
    return (
      <div className={styles.container}>
        <p>Cargando...</p>
      </div>
    );

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h1>Configuración</h1>
      </div>
      {message && (
        <div className={message.includes('Error') ? styles.errorMsg : styles.successMsg}>
          {message}
        </div>
      )}

      <div className={styles.statsGrid}>
        <div className={styles.statCard}>
          <h3>Plan Actual</h3>
          <p className={styles.planBadge}>{school?.plan === 'pro' ? 'Profesional' : 'Básico'}</p>
        </div>
        <div className={styles.statCard}>
          <h3>Alumnos</h3>
          <p>{school?.stats?.totalStudents ?? 0}</p>
        </div>
        <div className={styles.statCard}>
          <h3>Personal</h3>
          <p>{school?.stats?.totalStaff ?? 0}</p>
        </div>
        <div className={styles.statCard}>
          <h3>Grupos</h3>
          <p>{school?.stats?.totalGroups ?? 0}</p>
        </div>
      </div>

      <div className={styles.card}>
        <h2>Ciclo Escolar Vigente</h2>
        <div className={styles.fieldRow}>
          <label>Fecha de inicio</label>
          <input
            type="date"
            value={settings.schoolYearStart}
            onChange={(e) => updateSetting('schoolYearStart', e.target.value)}
            className={styles.input}
          />
        </div>
        <div className={styles.fieldRow}>
          <label>Fecha de fin</label>
          <input
            type="date"
            value={settings.schoolYearEnd}
            onChange={(e) => updateSetting('schoolYearEnd', e.target.value)}
            className={styles.input}
          />
        </div>
      </div>

      <div className={styles.card}>
        <h2>Periodo Vacacional — Alumnos</h2>
        <div className={styles.fieldRow}>
          <label>Inicio</label>
          <input
            type="date"
            value={settings.studentVacationStart}
            onChange={(e) => updateSetting('studentVacationStart', e.target.value)}
            className={styles.input}
          />
        </div>
        <div className={styles.fieldRow}>
          <label>Fin</label>
          <input
            type="date"
            value={settings.studentVacationEnd}
            onChange={(e) => updateSetting('studentVacationEnd', e.target.value)}
            className={styles.input}
          />
        </div>
      </div>

      <div className={styles.card}>
        <h2>Periodo Vacacional — Personal</h2>
        <div className={styles.fieldRow}>
          <label>Inicio</label>
          <input
            type="date"
            value={settings.staffVacationStart}
            onChange={(e) => updateSetting('staffVacationStart', e.target.value)}
            className={styles.input}
          />
        </div>
        <div className={styles.fieldRow}>
          <label>Fin</label>
          <input
            type="date"
            value={settings.staffVacationEnd}
            onChange={(e) => updateSetting('staffVacationEnd', e.target.value)}
            className={styles.input}
          />
        </div>
      </div>

      <div className={styles.card}>
        <h2>Costo de Colegiatura</h2>
        <div className={styles.fieldRow}>
          <label>Mensualidad</label>
          <div className={styles.inputGroup}>
            <span className={styles.inputPrefix}>$</span>
            <input
              type="number"
              min="0"
              step="0.01"
              value={settings.tuitionCost}
              onChange={(e) => updateSetting('tuitionCost', e.target.value)}
              className={styles.input}
              placeholder="0.00"
            />
          </div>
        </div>
      </div>

      <div className={styles.card}>
        <h2>Meses del Ciclo Escolar</h2>
        <p className={styles.cardSub}>
          Seleccioná los meses que se cobrarán — los que no estén marcados no aparecerán en el
          registro de pagos
        </p>
        {activeMonthLabels.length === 0 ? (
          <p style={{ color: '#999' }}>
            Definí las fechas de inicio y fin del ciclo para generar los meses.
          </p>
        ) : (
          <div className={styles.monthGrid}>
            {activeMonthLabels.map(({ ym, label }) => (
              <label key={ym} className={styles.monthChip}>
                <input
                  type="checkbox"
                  checked={settings.activeMonths.includes(ym)}
                  onChange={() => toggleMonth(ym)}
                />
                <span>{label}</span>
              </label>
            ))}
          </div>
        )}
        <p style={{ fontSize: '0.8rem', color: '#999', marginTop: '0.8rem' }}>
          {settings.activeMonths.length} mes{settings.activeMonths.length !== 1 ? 'es' : ''} activo
          {settings.activeMonths.length !== 1 ? 's' : ''}
        </p>
      </div>

      <div className={styles.card}>
        <h2>Conceptos de Cobro</h2>
        <p className={styles.cardSub}>
          Se asignarán a los alumnos al momento de inscribirlos, además de la colegiatura
        </p>
        <FeeConceptManager />
      </div>

      <div className={styles.card}>
        <h2>Grados y Grupos</h2>
        <p className={styles.cardSub}>
          Se reflejarán automáticamente en inscripciones y asignaciones
        </p>
        {gradeMsg && (
          <div className={gradeMsg.includes('Error') ? styles.errorMsg : styles.successMsg}>
            {gradeMsg}
          </div>
        )}
        <div className={styles.inlineForm}>
          <input
            value={newGrade}
            onChange={(e) => setNewGrade(e.target.value)}
            placeholder="Nuevo grado"
            className={styles.input}
          />
          <button onClick={addGrade} className={styles.addBtn}>
            Agregar
          </button>
        </div>
        <div className={styles.inlineForm}>
          <select
            value={newGroupGrade}
            onChange={(e) => setNewGroupGrade(e.target.value)}
            className={styles.input}
            style={{ maxWidth: 200 }}
          >
            <option value="">Seleccionar grado</option>
            {grades.map((g) => (
              <option key={g.id} value={g.id}>
                {g.name}
              </option>
            ))}
          </select>
          <input
            value={newGroup}
            onChange={(e) => setNewGroup(e.target.value)}
            placeholder="Grupo"
            className={styles.input}
            style={{ maxWidth: 120 }}
          />
          <button onClick={addGroup} className={styles.addBtn}>
            Agregar
          </button>
        </div>
        {loadingGrades ? (
          <p>Cargando...</p>
        ) : grades.length === 0 ? (
          <p style={{ color: '#999', marginTop: '1rem' }}>No hay grados configurados.</p>
        ) : (
          <div className={styles.gradeList}>
            {grades.map((g) => (
              <div key={g.id} className={styles.gradeItem}>
                <div className={styles.gradeHeader}>
                  <strong>{g.name}</strong>
                  <button onClick={() => deleteGrade(g.id)} className={styles.smallBtn}>
                    ✕
                  </button>
                </div>
                <div className={styles.groupList}>
                  {g.groups.length === 0 ? (
                    <span className={styles.emptyGroup}>Sin grupos</span>
                  ) : (
                    g.groups.map((gr) => (
                      <span key={gr.id} className={styles.groupTag}>
                        {gr.name}
                        <button
                          onClick={() => deleteGroup(g.id, gr.id)}
                          className={styles.tagRemove}
                        >
                          ✕
                        </button>
                      </span>
                    ))
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <button onClick={handleSave} disabled={saving} className={styles.saveBtn}>
        {saving ? 'Guardando...' : 'Guardar Configuración'}
      </button>
    </div>
  );
}

function FeeConceptManager() {
  const [fees, setFees] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: '', amount: '' });
  const [editingId, setEditingId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const fetchFees = async () => {
    setLoading(true);
    try {
      const data = await api.get('/api/fees');
      if (Array.isArray(data)) setFees(data);
    } catch {
      /* ignore */
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchFees();
  }, []);

  const resetForm = () => {
    setForm({ name: '', amount: '' });
    setEditingId(null);
    setShowForm(false);
    setError('');
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      if (editingId) {
        await api.put(`/api/fees/${editingId}`, {
          name: form.name,
          amount: parseFloat(form.amount),
        });
      } else {
        await api.post('/api/fees', { name: form.name, amount: parseFloat(form.amount) });
      }
      resetForm();
      fetchFees();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (f: any) => {
    await api.put(`/api/fees/${f.id}`, { isActive: !f.isActive });
    fetchFees();
  };

  const deleteFee = async (id: string) => {
    if (!confirm('¿Eliminar este concepto?')) return;
    await api.delete(`/api/fees/${id}`);
    fetchFees();
  };

  return (
    <div>
      {error && (
        <div className={styles.errorMsg} style={{ marginBottom: 8 }}>
          {error}
        </div>
      )}

      <button
        className={styles.addBtn}
        onClick={() => {
          resetForm();
          setShowForm(true);
        }}
      >
        + Nuevo Concepto
      </button>

      {showForm && (
        <form onSubmit={handleSave} className={styles.inlineForm} style={{ marginTop: 8 }}>
          <input
            placeholder="Nombre del concepto"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            required
            className={styles.input}
            style={{ maxWidth: 250 }}
          />
          <div className={styles.inputGroup} style={{ maxWidth: 150 }}>
            <span className={styles.inputPrefix}>$</span>
            <input
              type="number"
              step="0.01"
              min="0"
              placeholder="0.00"
              value={form.amount}
              onChange={(e) => setForm({ ...form, amount: e.target.value })}
              required
              className={styles.input}
            />
          </div>
          <button type="submit" disabled={saving} className={styles.addBtn}>
            {saving ? '...' : editingId ? 'Actualizar' : 'Crear'}
          </button>
          <button
            type="button"
            onClick={resetForm}
            className={styles.saveBtn}
            style={{ padding: '0.5rem 1rem', fontSize: '0.85rem' }}
          >
            Cancelar
          </button>
        </form>
      )}

      {loading ? (
        <p style={{ color: '#999', marginTop: 8 }}>Cargando...</p>
      ) : fees.length === 0 ? (
        <p style={{ color: '#999', marginTop: 8 }}>No hay conceptos configurados.</p>
      ) : (
        <div className={styles.gradeList} style={{ marginTop: 12 }}>
          {fees.map((f: any) => (
            <div key={f.id} className={styles.gradeItem}>
              <div className={styles.gradeHeader}>
                <div>
                  <strong>{f.name}</strong>
                  <span style={{ marginLeft: 12, color: '#1a1c2c', fontWeight: 600 }}>
                    ${f.amount.toFixed(2)}
                  </span>
                  <span
                    style={{
                      marginLeft: 8,
                      fontSize: '0.75rem',
                      padding: '0.2rem 0.5rem',
                      borderRadius: 12,
                      background: f.isActive ? '#e8f5e9' : '#fbe9e7',
                      color: f.isActive ? '#2e7d32' : '#c62828',
                      fontWeight: 600,
                    }}
                  >
                    {f.isActive ? 'Activo' : 'Inactivo'}
                  </span>
                </div>
                <div style={{ display: 'flex', gap: 4 }}>
                  <button
                    onClick={() => {
                      setForm({ name: f.name, amount: String(f.amount) });
                      setEditingId(f.id);
                      setShowForm(true);
                    }}
                    className={styles.smallBtn}
                  >
                    ✏️
                  </button>
                  <button onClick={() => toggleActive(f)} className={styles.smallBtn}>
                    {f.isActive ? '⏸' : '▶️'}
                  </button>
                  <button onClick={() => deleteFee(f.id)} className={styles.smallBtn}>
                    🗑️
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
